import { faSpinner, faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    FormControlLabel,
    Grid,
    TextField
} from '@mui/material';
import { FlexContainer, SelectField } from 'components';
import { UpdateRolesForm } from 'components/Form';
import { ModalHeader } from 'components/Modals/ModalHeader';
import { useFetch, useStores } from 'hooks';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { UsersService } from 'services';
import { FormHelper, translate } from 'utils/helpers';

export const CreateUserModal = ({
                                    onClose,
                                    onConfirm,
                                    closeOnSubmit
                                }) => {
    const { enqueueSnackbar } = useSnackbar();
    const { userStore } = useStores();
    const {
        currentCompany,
        scope
    } = userStore;

    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
        getValues,
        watch,
        setError,
        clearErrors
    } = useForm({
        mode: 'onBlur',
        defaultValues: {
            accountActivated: false,
            mainCompany: null,
            roles: [],
            email: null,
            firstName: '',
            lastName: '',
            login: '',
            attestationNumber: ''
        }
    });

    const accountActivated = watch('accountActivated');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [accessibleRolesByStructureLevel, setAccessibleRolesByStructureLevel] = useState(new Map());
    const [isLoadingRoles, setIsLoadingRoles] = useState(false);
    const { response: companies } = useFetch(() => UsersService.getCompanyChildrenOptions(), []);

    useEffect(() => {
        const mainCompany = scope?.company;
        if (accountActivated && mainCompany) {
            setIsLoadingRoles(true);
            UsersService.getAccessibleRolesWithStructureLevel(mainCompany?.value)
                .then((response) => {
                    const map = new Map();
                    Object.values(response).forEach((value) => map.set(value.structureLevelOption, value.roleSelectItems));
                    setAccessibleRolesByStructureLevel(map);
                })
                .finally(() => setIsLoadingRoles(false));
        }
    }, [accountActivated, scope?.company]);

    const createUser = useCallback((formData) => {
        setIsSubmitting(true);
        UsersService.createUser(formData)
            .then(response => {
                response && onConfirm(response);
                if (closeOnSubmit) {
                    onClose();
                }
            })
            .catch(error => enqueueSnackbar(error?.message || error, { variant: 'error' }))
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [closeOnSubmit, onClose, onConfirm, enqueueSnackbar]);

    const onSubmit = (data) => {
        const formData = {
            login: data.login,
            password: data.password,
            person: {
                firstName: data.firstName,
                lastName: data.lastName,
                attestationNumber: data.attestationNumber,
                mainCompany: data.mainCompany || currentCompany,
                contact: {
                    technicalEmail: data.email
                }
            },
            accountActivated: data.accountActivated,
            roles: data.roles
        };

        createUser(formData);
    };

    useEffect(() => {
        if (!accountActivated) {
            clearErrors('login');
        }
    }, [accountActivated, clearErrors]);

    return (
        <form
            autoComplete="off"
            name="createUserForm"
            style={{ overflow: 'auto' }}
            onSubmit={handleSubmit(onSubmit)}
        >
            <ModalHeader onClose={onClose}>
                <FontAwesomeIcon icon={faUserPlus}/>
                {translate('modalUser.newUser')}
            </ModalHeader>

            <DialogContent style={{ minWidth: '400px', padding: '10px' }}>
                <Grid container direction="row" item spacing={2} style={{ maxWidth: '1400px' }}>
                    <Grid item style={{ width: '500px' }}>
                        <FlexContainer flexDirection="column">
                            <Controller
                                control={control}
                                name="accountActivated"
                                render={({ field: { value, onChange, ...field } }) => (
                                    <FormControlLabel
                                        control={<Checkbox checked={value || false} color="primary" onChange={(e) => onChange(e.target.checked)} {...field} />}
                                        label={translate('common.userAccount')}
                                    />
                                )}
                            />

                            {Boolean(companies?.length) && (
                                <Controller
                                    control={control}
                                    name="mainCompany"
                                    rules={{ required: 'Ce champ est requis' }}
                                    render={({ field, fieldState: { error } }) => (
                                        <SelectField
                                            {...field}
                                            id="mainCompany"
                                            label="common.company"
                                            name="mainCompany"
                                            options={companies}
                                            required
                                            error={!!error}
                                            helperText={error?.message}
                                        />
                                    )}
                                />
                            )}

                            <Controller
                                control={control}
                                name="lastName"
                                rules={{ required: 'Ce champ est requis' }}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        autoComplete="off"
                                        autoFocus
                                        error={!!error}
                                        helperText={error?.message}
                                        label={translate('common.lastName')}
                                        required
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                name="firstName"
                                rules={{ required: 'Ce champ est requis' }}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        autoComplete="new-password"
                                        error={!!error}
                                        helperText={error?.message}
                                        label={translate('common.firstName')}
                                        required
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                name="email"
                                rules={{
                                    required: 'Ce champ est requis',
                                    validate: value => FormHelper.validateEmail(value) || 'Email invalide'
                                }}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        autoComplete="new-password"
                                        error={!!error}
                                        helperText={error?.message}
                                        label={translate('common.email')}
                                        required
                                        type="email"
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                name="attestationNumber"
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        autoComplete="new-password"
                                        error={!!error}
                                        helperText={error?.message}
                                        label={translate('common.capacityAttestationNumber')}
                                    />
                                )}
                            />

                            {accountActivated && (
                                <>
                                    <Controller
                                        control={control}
                                        name="login"
                                        rules={{ required: accountActivated ? 'Ce champ est requis' : false }}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                autoComplete="new-password"
                                                disabled={!accountActivated}
                                                error={!!error}
                                                helperText={error?.message}
                                                label={translate('common.login')}
                                                required={accountActivated}
                                            />
                                        )}
                                    />
                                    <br/>
                                </>
                            )}
                        </FlexContainer>
                    </Grid>
                    {accountActivated && (
                        <Grid item style={{
                            maxHeight: '800px',
                            width: '550px'
                        }}>
                            {isLoadingRoles ? (
                                <FontAwesomeIcon icon={faSpinner} spin />
                            ) : (
                                <UpdateRolesForm
                                    accessibleRolesByStructureLevel={accessibleRolesByStructureLevel}
                                    control={control}
                                    watch={watch}
                                    setValue={setValue}
                                    getValues={getValues}
                                    formState={{ setField: (name, value) => setValue(name, value) }}
                                    initialRoles={getValues('roles')}
                                />
                            )}
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>{translate('button.cancel')}</Button>
                <Button color="primary" disabled={isSubmitting} type="submit">
                    {isSubmitting ? <FontAwesomeIcon icon={faSpinner} spin/> : translate('common.create')}
                </Button>
            </DialogActions>
        </form>
    );
};

CreateUserModal.propTypes = {
    closeOnSubmit: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired
};

CreateUserModal.defaultProps = {
    closeOnSubmit: true
};